<template>
    <div class="print-container" :style="paperSizeStyle">
        <!--Header-->
        <div class="header">
            <div class="title">
                Factsheet {{ planNaam }} {{ gemeenteNaam }} - Kavel {{ ibisId }}
            </div>
            <div class="logo-container">
                <img
                    :src="menuComponents.logo.imageUrl"
                    :alt="menuComponents.logo.logo"
                    class="image"
                />
            </div>
        </div>
        <!--Map + legend + info-->
        <div class="map-container mb-3">
            <mini-map
                :id="miniMapId"
                :ref="miniMapId"
                class="map-preview"
                :showControls="false"
            />
            <div class="ml-3 right-side">
                <div>
                    <span class="title">Legenda</span>
                    <div class="data">
                        <legend-component
                            class="map-legend"
                            :legendData="legendData"
                        />
                    </div>
                </div>
                <div class="mt-auto">
                    <span class="title">Kavelkenmerken</span>
                    <object-detail
                        v-if="kavelObject"
                        class="data"
                        :displayObject="kavelKenmerken"
                        extendedCellClass="printDataCell"
                    />
                </div>
            </div>
        </div>
        <!--Data-->
        <div class="content">
            <div class="w-50 mr-3">
                <div class="terrain_features mb-2">
                    <span class="title">Terreinkenmerken</span>
                    <object-detail
                        v-if="terreinKenmerken"
                        class="data"
                        :displayObject="terreinKenmerken"
                        extendedCellClass="printDataCell"
                    />
                </div>
                <div class="milieu">
                    <span class="title">Milieuzonering</span>
                    <object-detail
                        v-if="milieuZonering"
                        class="data"
                        :displayObject="milieuZonering"
                        extendedCellClass="printDataCell"
                    />
                </div>
            </div>
            <div class="w-50">
                <div class="mb-2">
                    <span class="title">Terreinoppervlakte</span>
                    <object-detail
                        v-if="terreinOppervlakte"
                        class="data"
                        :displayObject="terreinOppervlakte"
                        extendedCellClass="printDataCell"
                    />
                </div>
                <div>
                    <span class="title">Bedrijven</span>
                    <object-detail
                        v-if="bedrijven"
                        class="data"
                        :displayObject="bedrijven"
                        extendedCellClass="printDataCell printDataCellCompanies"
                    />
                </div>
            </div>
        </div>
        <!--QR code-->
        <div class="qr-code mt-2">
            <qrcode-vue
                :value="qrCodeUrl"
                :size="65"
                level="H"
                render-as="svg"
            />
        </div>
        <!--Disclaimer-->
        <div class="disclaimer">
            <b>Disclaimer:</b> De Provincie Gelderland stelt deze informatie met
            zorg samen, maar aan de inhoud kunnen <br />geen rechten worden
            ontleend.
        </div>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import {
        MAP_MARGINS,
        DEFAULT_MIN_ZOOM_RESOLUTION,
        NEDGLOBE_VIEWER
    } from '../../../constants';
    import { paperSizes, PAPER_FORMATS } from '../../../constants/print';
    import MiniMap from '../../geo/maps/Mini.vue';
    import ObjectDetail from '../../geo/information/feature/ObjectDetail';
    import Legend from '../../geo/panels/legend';
    import QrcodeVue from 'qrcode.vue';

    export default {
        components: {
            'object-detail': ObjectDetail,
            'legend-component': Legend,
            'mini-map': MiniMap,
            'qrcode-vue': QrcodeVue
        },
        props: {
            displayObjectPrimary: { type: Object, required: true },
            displayObjectSecondary: { type: Object, required: true },
            qrCodeUrl: { type: String, required: true }
        },
        created() {
            this.$_showFeatureOnMap();
        },
        data() {
            return {
                miniMapId: 'report-print-preview-gelderland'
            };
        },
        computed: {
            ...mapState(['uiElements']),
            ...mapGetters(['getLegendData', 'getOrganizationId']),
            menuComponents() {
                return this.uiElements.menuBar.components;
            },
            legendData() {
                const allowed = [
                    this.displayObjectPrimary.layerTitle,
                    this.displayObjectSecondary.layerTitle
                ];

                return this.getLegendData().filter((item) =>
                    allowed.includes(item.title)
                );
            },
            kavelObject() {
                return this.displayObjectPrimary;
            },
            terreinObject() {
                return this.displayObjectSecondary;
            },
            planNaam() {
                return this.terreinObject.info.find(
                    (i) => i.attribute === 'a_plannaam'
                )?.displayValue;
            },
            gemeenteNaam() {
                return this.terreinObject.info.find(
                    (i) => i.attribute === 'gemeente_naam'
                )?.displayValue;
            },
            ibisId() {
                return this.kavelObject.info.find((i) => i.attribute === 'ibis_id')
                    ?.displayValue;
            },
            kavelKenmerken() {
                const allowed = [
                    { attribute: 'ibis_id', alias: 'IBIS ID' },
                    { attribute: 'milieuwet_code', alias: 'Milieuwet code' },
                    {
                        attribute: 'kaveloppervlak',
                        alias: 'Kaveloppervlak',
                        unit: 'm²'
                    },
                    { attribute: 'status', alias: 'Status' }
                ];
                return {
                    ...this.kavelObject,
                    info: allowed.flatMap(({ attribute, alias, unit }) => {
                        const item = this.kavelObject.info.find(
                            (i) => i.attribute === attribute
                        );
                        if (!item) {
                            return [];
                        }
                        return {
                            ...item,
                            alias,
                            displayValue:
                                item.displayValue && unit
                                    ? `${item.displayValue} ${unit}`
                                    : item.displayValue
                        };
                    })
                };
            },
            terreinKenmerken() {
                const allowed = [
                    { attribute: 'a_plannaam', alias: 'Plannaam' },
                    { attribute: 'terreingemeenteregio', alias: 'Regio' },
                    { attribute: 'ibis_id', alias: 'Terrein ID' },
                    { attribute: 'a_type', alias: 'Terrein type' },
                    { attribute: 'a_planfase', alias: 'Planfase' },
                    { attribute: 'c_hyperlink', alias: 'Website' }
                ];
                return {
                    ...this.terreinObject,
                    info: allowed.flatMap(({ attribute, alias }) => {
                        const item = this.terreinObject.info.find(
                            (i) => i.attribute === attribute
                        );
                        if (!item) {
                            return [];
                        }
                        return {
                            ...item,
                            alias
                        };
                    })
                };
            },
            terreinOppervlakte() {
                const allowed = [
                    { attribute: 'bruto_ha', alias: 'Bruto', unit: 'ha' },
                    { attribute: 'netto_ha', alias: 'Netto', unit: 'ha' },
                    { attribute: 'optie_ha', alias: 'Optie', unit: 'ha' },
                    {
                        attribute: 'direcht_uitgeefbaar_ha',
                        alias: 'Uitgeefbaar',
                        unit: 'ha'
                    },
                    {
                        attribute: 'op_termijn_uitgeefbaar_ha',
                        alias: 'Niet-terstond uitgeefbaar',
                        unit: 'ha'
                    }
                ];
                return {
                    ...this.terreinObject,
                    info: allowed.flatMap(({ attribute, alias, unit }) => {
                        const item = this.terreinObject.info.find(
                            (i) => i.attribute === attribute
                        );
                        if (!item) {
                            return [];
                        }
                        return {
                            ...item,
                            alias,
                            displayValue:
                                item.displayValue && unit
                                    ? `${item.displayValue} ${unit}`
                                    : item.displayValue
                        };
                    })
                };
            },
            milieuZonering() {
                const allowed = [
                    { attribute: 'percentage_m1_0', alias: '1.0', unit: '%' },
                    { attribute: 'percentage_m2_0', alias: '2.0', unit: '%' },
                    { attribute: 'percentage_m3_1', alias: '3.1', unit: '%' },
                    { attribute: 'percentage_m3_2', alias: '3.2', unit: '%' },
                    { attribute: 'percentage_m4_1', alias: '4.1', unit: '%' },
                    { attribute: 'percentage_m4_2', alias: '4.2', unit: '%' },
                    { attribute: 'percentage_m5_1', alias: '5.1', unit: '%' },
                    { attribute: 'percentage_m5_2', alias: '5.2', unit: '%' },
                    { attribute: 'percentage_m1_0', alias: '6.0', unit: '%' }
                ];
                return {
                    ...this.terreinObject,
                    info: allowed.flatMap(({ attribute, alias, unit }) => {
                        const item = this.terreinObject.info.find(
                            (i) => i.attribute === attribute
                        );
                        if (!item) {
                            return [];
                        }
                        return {
                            ...item,
                            alias,
                            displayValue:
                                item.displayValue && unit
                                    ? `${item.displayValue} ${unit}`
                                    : item.displayValue
                        };
                    })
                };
            },
            bedrijven() {
                const allowed = [{ attribute: 'bedrijven', alias: '' }];
                return {
                    ...this.terreinObject,
                    info: allowed.flatMap(({ attribute, alias, unit }) => {
                        const item = this.terreinObject.info.find(
                            (i) => i.attribute === attribute
                        );
                        if (!item) {
                            return [];
                        }
                        return {
                            ...item,
                            alias,
                            displayValue: item.displayValue
                                .split('\n')
                                .map((line) => `- ${line.trim()}`)
                                .join('\n')
                        };
                    })
                };
            },
            paperSizeStyle() {
                const foundSize = paperSizes.find(
                    (paperFormat) => paperFormat.name === PAPER_FORMATS.A4_PORTRAIT
                );

                const margin = 10;
                const height = foundSize.height - margin * 2;
                const width = foundSize.width - margin * 2;

                return {
                    top: `${margin}mm`,
                    left: `${margin}mm`,
                    width: `${width}mm`,
                    // -1 prevents extra page
                    height: `${height - 1}mm`,
                    overflow: 'hidden'
                };
            }
        },
        methods: {
            async $_showFeatureOnMap() {
                if (this.kavelObject.feature && this.terreinObject.feature) {
                    this.$nextTick(async () => {
                        // Add 2 layers with it's style
                        await this.$store.dispatch('addLayerToMap', {
                            mapName: this.miniMapId,
                            id: this.kavelObject.layerId
                        });
                        await this.$store.dispatch('addLayerToMap', {
                            mapName: this.miniMapId,
                            id: this.terreinObject.layerId
                        });

                        // Copy current selected kavel to minimap
                        const miniMapConfig = this.$store.getters.getMapByName(
                            this.miniMapId
                        );
                        const mainMapConfig =
                            this.$store.getters.getMapByName('main');
                        miniMapConfig.customLayers[
                            NEDGLOBE_VIEWER.customLayers.printable.selections
                        ]
                            .getSource()
                            .addFeatures(
                                mainMapConfig.customLayers[
                                    NEDGLOBE_VIEWER.customLayers.printable
                                        .selections
                                ]
                                    .getSource()
                                    .getFeatures()
                                    .filter((f) => f.get('ibis_id') === this.ibisId)
                            );

                        // Adjust the maxresolution to prevent the layer from being invisible
                        miniMapConfig.layers.forEach((layer) => {
                            layer.olLayer.setMaxResolution(53.76); // => scale = 192000
                        });

                        // Zoom in on terrein feature
                        const geometry = this.terreinObject.feature.getGeometry();
                        const dataExtent = geometry.getExtent();
                        const margin = MAP_MARGINS.featuresImported;
                        const view = miniMapConfig.map.getView();
                        const fitOptions = {
                            padding: [margin, margin, margin, margin],
                            minResolution: DEFAULT_MIN_ZOOM_RESOLUTION
                        };
                        view.fit(dataExtent, fitOptions);

                        // Finished
                        miniMapConfig.map.once('rendercomplete', async () => {
                            this.$emit('mapReady');
                            this.$emit('imageReady');
                        });
                    });
                }
            }
        }
    };
</script>

<style lang="scss">
.printDataCell {
    width: auto !important;
    padding: 1px 10px 1px 5px !important;
    line-height: inherit !important;
    font-size: 85% !important;
}
.printDataCellCompanies {
    padding: 1px !important;
    font-weight: normal !important;
    white-space: pre-line;
}
</style>
<style scoped lang="scss">
.print-container {
    height: 100%;
    display: flex;
    flex-direction: column;

    .header {
        display: flex;
        justify-content: space-between;
        padding-bottom: 5px;

        .title {
            align-items: center;
            display: flex;
            font-weight: bold;
            font-size: 22px;
        }

        .logo-container {
            align-items: center;
            display: flex;

            .image {
                background-size: contain;
                background-repeat: no-repeat;
                height: 45px;
            }
        }
    }

    span.title {
        font-weight: bold;
    }

    .content {
        display: flex;
        overflow: hidden;
        height: 100%;
    }

    .data {
        border: 1px solid $gray-04;
        display: block;
        width: initial;
    }

    .map-container {
        display: flex;

        .map-preview {
            border: 1px solid $gray-04;
            width: 400px;
            height: 400px;
            min-height: initial;
        }

        .right-side {
            flex: 1;
            display: flex;
            flex-direction: column;
            margin-top: -7px;

            .map-legend {
                margin: -12px;
                line-height: 20px;
                font-size: 85%;
            }
        }
    }

    .qr-code {
        display: flex;

        div {
            margin-left: auto;
        }
    }

    .disclaimer {
        width: 85%;
        font-size: 85%;
        margin: auto;
        text-align: center;
    }
}
</style>
